/* eslint-disable max-lines -- the rule was disabled before a comment became required */
import {
  CancellationStatus,
  type DocumentsToDownload,
  type NowDocumentsToDownload,
  type UserDocumentPosition,
  type UserOrderCarrierV2,
  type UserOrderDocument,
  type UserOrderDocumentERx,
  type UserOrderResponseV2,
  type UserOrderResponseWithFlags,
  type UserOrderResponseWithFlagsSubOrdersInner,
  UserSubOrder1EnrichedStatusEnum,
  UserSubOrderEnrichedStatusEnum,
  type UserSubOrderV2,
  UserSubOrderV2EnrichedStatusEnum,
} from '@redteclab/api/clients/bully'

import { type PublicGlobalConfig } from '../../global-config'
import {
  urlResolverGetInvoiceByOrderId,
  urlResolverGetMarketPlaceOrderInvoiceByOrderId,
  urlResolverGetNowOrderInvoiceByOrderId,
  urlResolverGetSubOrderInvoicesBySubOrderId,
  urlResolverRequestMarketPlaceOrderInvoiceByOrderId,
} from '../../url-handling'

import {
  ApiContractAndParcelLabStatuses,
  type OrderStatusConfigLabels,
} from './AccountOverviewLastOrderShippingStatusTypes'
import { DOCUMENT_TYPE_INVOICE } from './DOCUMENT_TYPE_INVOICE'
import { SELLER_ID_SAE } from './SELLER_ID_SAE'

export const userSubOrderAccessorIsOrderDelivered = (
  subOrder: UserOrderResponseWithFlagsSubOrdersInner | UserSubOrderV2,
): boolean =>
  subOrder.enrichedStatus === UserSubOrder1EnrichedStatusEnum.Dispatched

export const userSubOrderAccessorIsCancellationRequested = (
  subOrder: UserOrderResponseWithFlagsSubOrdersInner | UserSubOrderV2,
): boolean =>
  subOrder.cancellationStatus === CancellationStatus.Requested ||
  subOrder.cancellationStatus === CancellationStatus.PartialRequested

export const userSubOrderAccessorGetInvoiceItems = (
  subOrder: UserSubOrderV2,
): (DocumentsToDownload | NowDocumentsToDownload)[] => {
  const subOrdersWithInvoice = subOrder.documentsToDownload?.filter((item) => {
    return item.type?.toLowerCase() === DOCUMENT_TYPE_INVOICE
  })

  const nowOrderSubOrderWithInvoice = subOrder.nowDocumentsToDownload?.filter(
    (item) => {
      return item.documentType?.toLowerCase() === DOCUMENT_TYPE_INVOICE
    },
  )

  return [
    ...(nowOrderSubOrderWithInvoice ?? []),
    ...(subOrdersWithInvoice ?? []),
  ]
}

const getItemsAndThumbnailsFromOrderDocuments = (
  orderDocuments: UserOrderDocument[],
): {
  hashedToken?: string
  isERx?: boolean
  itemName?: string
  itemQuantity?: string
  offerId?: string
  productTotalCost?: number
  pzn?: string
  subOrderId?: string
  thumbnailURL?: string
}[] => {
  return orderDocuments.flatMap((orderDocument) => {
    if (orderDocument.documentPositions) {
      return orderDocument.documentPositions.map((item) => {
        const quantity = [
          `${item.packagingQuantity} ${item.packagingUnit}`,
          item.pharmaForm,
        ]
          .filter(Boolean)
          .join(' | ')

        return {
          itemName: item.itemName,
          itemQuantity: quantity,
          offerId: item.offerId,
          productTotalCost:
            item.salesPrice && item.quantity
              ? item.salesPrice * item.quantity
              : item.salesPrice,
          pzn: item.pzn,
          thumbnailURL: item.thumbnailURL,
        }
      })
    }

    return []
  })
}

const getItemsAndThumbnailsFromOrderDocumentsErx = (
  orderDocumentsErx: UserOrderDocumentERx[],
): {
  hashedToken?: string
  isERx?: boolean
  itemName?: string
  itemQuantity?: string
  offerId?: string
  productTotalCost?: number
  pzn?: string
  subOrderId?: string
  thumbnailURL?: string
}[] => {
  return orderDocumentsErx.flatMap((orderDocumentErx) => {
    if (orderDocumentErx.documentPositions) {
      return orderDocumentErx.documentPositions.map((item) => {
        // The construction of the quantity is as a result of the requirements of the ticket (WSCNL-9487)
        // as seen in figma here:- (https://www.figma.com/design/uuADxtSwdpdIe9XFWt62BI/branch/7XzfyuqvXWKCcx8oYNOdJL/Help-Center?node-id=6286-11988&m=dev)
        // e.g 120 St | Hartkapseln -> 'Packaging Quantity' 'Packaging Unit' |  'Form' (Injection/tablet etc)
        const quantity = [
          `${item.packagingQuantity} ${item.originalPrescriptionProduct?.packagingUnit}`,
          item.pharmaForm,
        ]
          .filter(Boolean)
          .join(' | ')

        return {
          hashedToken: item.hashedToken,
          isERx: true,
          itemName: item.itemName,
          itemQuantity: quantity,
          pzn: item.pzn,
        }
      })
    }

    return []
  })
}

export const userSubOrderAccessorGetMultipleProductThumbnailAndName = (
  order: UserOrderResponseV2 | UserOrderResponseWithFlags,
): {
  hashedToken?: string
  isERx?: boolean
  itemName?: string
  itemQuantity?: string
  offerId?: string
  productTotalCost?: number
  pzn?: string
  thumbnailURL?: string
}[] => {
  const { subOrders } = order
  if (!subOrders?.length) {
    return []
  }

  return subOrders.flatMap((subOrder) => [
    ...(subOrder.orderDocuments?.length
      ? getItemsAndThumbnailsFromOrderDocuments(subOrder.orderDocuments)
      : []),
    ...(subOrder.orderDocumentsERx?.length
      ? getItemsAndThumbnailsFromOrderDocumentsErx(subOrder.orderDocumentsERx)
      : []),
  ])
}

export const userSubOrderAccessorIsNowPickupOrder = (
  subOrder: UserOrderResponseWithFlagsSubOrdersInner | UserSubOrderV2,
): boolean => {
  if (!subOrder.carrier?.nowData) {
    return false
  }

  return 'slotPickup' in subOrder.carrier.nowData
}

export const userSubOrderAccessorGetStatusBasedOnEstimatedDate = ({
  estimatedDate,
  orderDeliveredStatus,
  subOrderStatus,
  timeRange,
}: {
  estimatedDate?: string
  orderDeliveredStatus?: boolean
  subOrderStatus: string
  timeRange?: string | false
}): OrderStatusConfigLabels => {
  if (subOrderStatus !== UserSubOrderEnrichedStatusEnum.Dispatched) {
    return (
      Object.values(ApiContractAndParcelLabStatuses).find(
        (status) => status === subOrderStatus,
      ) ?? UserSubOrderEnrichedStatusEnum.Processing
    )
  }

  if (orderDeliveredStatus) {
    return ApiContractAndParcelLabStatuses.Delivered
  }

  const isEstimatedDateOrTimeRange = Boolean(estimatedDate ?? timeRange)

  return isEstimatedDateOrTimeRange
    ? ApiContractAndParcelLabStatuses.Shipped
    : UserSubOrderV2EnrichedStatusEnum.Dispatched
}

export const orderIsShipped = (subOrder: UserSubOrderV2): boolean => {
  const { enrichedStatus } = subOrder

  if (!enrichedStatus) {
    return false
  }

  return (
    enrichedStatus === UserSubOrderV2EnrichedStatusEnum.Closed ||
    enrichedStatus === UserSubOrderV2EnrichedStatusEnum.Dispatched
  )
}

export const getUrlForSingleInvoice = (
  invoice: DocumentsToDownload | NowDocumentsToDownload,
  config: PublicGlobalConfig,
  subOrderId: string,
): string | undefined => {
  if (
    'content' in invoice &&
    invoice.content &&
    'hash' in invoice.content &&
    invoice.content.hash
  ) {
    return urlResolverGetInvoiceByOrderId(
      config,
      subOrderId,
      invoice.content.hash,
    )
  }

  if ('documentId' in invoice && invoice.documentId && invoice.orderId) {
    return urlResolverGetNowOrderInvoiceByOrderId(config, {
      documentId: invoice.documentId,
      orderId: invoice.orderId,
      subOrderId,
    })
  }
  if (
    'content' in invoice &&
    invoice.content &&
    'document_id' in invoice.content &&
    invoice.content.document_id &&
    invoice.order_id
  ) {
    return urlResolverGetMarketPlaceOrderInvoiceByOrderId(config, {
      documentId: invoice.content.document_id,
      subOrderId,
    })
  }

  return undefined
}

const getUrlForMultipleInvoices = (
  config: PublicGlobalConfig,
  subOrder: UserSubOrderV2,
): string | undefined => {
  if (!subOrder.subOrderId) {
    return undefined
  }
  if (subOrder.documentsToDownload?.length) {
    const [{ order_id: orderId }] = subOrder.documentsToDownload

    return (
      orderId &&
      urlResolverGetSubOrderInvoicesBySubOrderId(
        config,
        subOrder.subOrderId,
        orderId,
      )
    )
  }
  if (subOrder.nowDocumentsToDownload?.length) {
    const [{ orderId }] = subOrder.nowDocumentsToDownload

    return (
      orderId &&
      urlResolverGetSubOrderInvoicesBySubOrderId(
        config,
        subOrder.subOrderId,
        orderId,
      )
    )
  }

  return undefined
}

export const userSubOrderAccessorGetUrlForSingleOrMultipleInvoices = (
  subOrder: UserSubOrderV2,
  config: PublicGlobalConfig,
  orderId: string,
): string | undefined => {
  const invoices = userSubOrderAccessorGetInvoiceItems(subOrder)
  if (!subOrder.subOrderId) {
    return undefined
  }

  if (invoices.length === 0 && subOrder.sellerId !== SELLER_ID_SAE) {
    return urlResolverRequestMarketPlaceOrderInvoiceByOrderId(config, {
      orderId,
      subOrderId: subOrder.subOrderId,
    })
  }

  if (invoices.length === 1) {
    return getUrlForSingleInvoice(invoices[0], config, subOrder.subOrderId)
  }

  if (invoices.length > 1) {
    return getUrlForMultipleInvoices(config, subOrder)
  }

  return undefined
}

export const userSubOrderAccessorIsNow = (
  subOrder: UserOrderResponseWithFlagsSubOrdersInner | UserSubOrderV2,
): boolean => {
  if (!subOrder.carrier?.nowData) {
    return false
  }

  return (
    'slot' in subOrder.carrier.nowData ||
    userSubOrderAccessorIsNowPickupOrder(subOrder)
  )
}

export const userSubOrderAccessorIsSellerMarketplace = (
  subOrder: UserSubOrderV2,
): boolean =>
  subOrder.sellerId !== SELLER_ID_SAE && !userSubOrderAccessorIsNow(subOrder)

export const userSubOrderGetNowOrderTimeRange = (
  subOrder: UserOrderResponseWithFlagsSubOrdersInner | UserSubOrderV2,
): string | undefined => {
  if (userSubOrderAccessorIsNow(subOrder) && subOrder.carrier?.nowData) {
    if ('slot' in subOrder.carrier.nowData) {
      return subOrder.carrier.nowData.slot?.timeString
    }

    if ('slotPickup' in subOrder.carrier.nowData) {
      return subOrder.carrier.nowData.slotPickup?.timeString
    }
  }

  return undefined
}

const userSubOrderGetOrderDocuments = (
  subOrder: UserOrderResponseWithFlagsSubOrdersInner | UserSubOrderV2,
): UserOrderDocument[] => {
  if (subOrder.orderDocuments) {
    return subOrder.orderDocuments
  }
  throw new Error('orderDocuments is undefined')
}

const userSubOrderGetDocumentPositionFromOrderDocument = (
  orderDocument: UserOrderDocument,
): UserDocumentPosition[] => {
  if (orderDocument.documentPositions) {
    return orderDocument.documentPositions
  }
  throw new Error('documentPositions is undefined')
}

export const userSubOrderGetIsSubOrderCancellablePerItem = (
  subOrder: UserOrderResponseWithFlagsSubOrdersInner | UserSubOrderV2,
): boolean => {
  const orderDocuments = userSubOrderGetOrderDocuments(subOrder)
  const documentPositions: UserDocumentPosition[] = []

  for (const orderDoc of orderDocuments) {
    documentPositions.push(
      ...userSubOrderGetDocumentPositionFromOrderDocument(orderDoc),
    )
  }

  return documentPositions.some((docPosition) => docPosition.isCancellable)
}

type Claimables = {
  claimable: UserDocumentPosition[]
  nonClaimable: UserDocumentPosition[]
}

type Cancellables = {
  cancellables: UserDocumentPosition[]
  nonCancellables: UserDocumentPosition[]
}

type Returnables = {
  nonReturnables: UserDocumentPosition[]
  returnables: UserDocumentPosition[]
}

const userSubOrderAccessorGetDamagedProductsClaimablesFromOrderDocs = (
  subOrder: UserSubOrderV2,
): Claimables => {
  const { orderDocuments: orderItems = [] } = subOrder

  const claimableOrderItems: UserDocumentPosition[] = []
  const nonClaimableOrderItems: UserDocumentPosition[] = []

  for (const orderItem of orderItems) {
    const docPositions = orderItem.documentPositions ?? []
    for (const docPosition of docPositions) {
      const { isClaimable } = docPosition
      if (isClaimable) {
        claimableOrderItems.push(docPosition)
      } else {
        nonClaimableOrderItems.push(docPosition)
      }
    }
  }

  return {
    claimable: claimableOrderItems,
    nonClaimable: nonClaimableOrderItems,
  }
}

const userSubOrderAccessorGetDamagedProductsClaimablesFromOrderDocsErx = (
  subOrder: UserSubOrderV2,
): Claimables => {
  const { orderDocumentsERx: orderItemsErx = [] } = subOrder

  const claimableOrderItems: UserDocumentPosition[] = []
  const nonClaimableOrderItems: UserDocumentPosition[] = []

  for (const orderItemErx of orderItemsErx) {
    const docPositions = orderItemErx.documentPositions ?? []
    for (const docPosition of docPositions) {
      const { isClaimable } = docPosition
      if (isClaimable) {
        claimableOrderItems.push(docPosition)
      } else {
        nonClaimableOrderItems.push(docPosition)
      }
    }
  }

  return {
    claimable: claimableOrderItems,
    nonClaimable: nonClaimableOrderItems,
  }
}

export const userSubOrderAccessorGetDamagedProductsClaimables = (
  subOrder: UserSubOrderV2,
): Claimables => {
  const { claimable: orderDocsClaimable, nonClaimable: orderDocsNonClaimable } =
    userSubOrderAccessorGetDamagedProductsClaimablesFromOrderDocs(subOrder)

  const {
    claimable: orderDocsErxClaimable,
    nonClaimable: orderDocsErxNonClaimable,
  } = userSubOrderAccessorGetDamagedProductsClaimablesFromOrderDocsErx(subOrder)

  return {
    claimable: [...orderDocsClaimable, ...orderDocsErxClaimable],
    nonClaimable: [...orderDocsNonClaimable, ...orderDocsErxNonClaimable],
  }
}

export const userSubOrderAccessorGetProductsCancellationCancellables = (
  subOrder: UserSubOrderV2,
): Cancellables => {
  const { orderDocuments = [] } = subOrder

  const cancellableOrderItems: UserDocumentPosition[] = []
  const nonCancellableOrderItems: UserDocumentPosition[] = []

  for (const orderItem of orderDocuments) {
    const docPositions = orderItem.documentPositions ?? []
    for (const docPosition of docPositions) {
      const { isCancellable } = docPosition
      if (isCancellable) {
        cancellableOrderItems.push(docPosition)
      } else {
        nonCancellableOrderItems.push(docPosition)
      }
    }
  }

  return {
    cancellables: [...cancellableOrderItems],
    nonCancellables: [...nonCancellableOrderItems],
  }
}

export const userSubOrderAccessorGetProductsReturnReturnables = (
  subOrder: UserSubOrderV2,
): Returnables => {
  const { orderDocuments = [] } = subOrder

  const returnableOrderItems: UserDocumentPosition[] = []
  const nonReturnableOrderItems: UserDocumentPosition[] = []
  for (const orderItem of orderDocuments) {
    const docPositions = orderItem.documentPositions ?? []
    for (const docPosition of docPositions) {
      const { isReturnable } = docPosition
      if (isReturnable) {
        returnableOrderItems.push(docPosition)
      } else {
        nonReturnableOrderItems.push(docPosition)
      }
    }
  }

  return {
    nonReturnables: nonReturnableOrderItems,
    returnables: returnableOrderItems,
  }
}

export const userSubOrderAccessorGetSubOrderId = (
  subOrder: UserSubOrderV2,
): string => {
  if (!subOrder.subOrderId) {
    throw new Error('subOrderId not found in subOrder')
  }

  return subOrder.subOrderId
}

export const userSubOrderAccessorGetCarrier = (
  subOrder: UserSubOrderV2,
): UserOrderCarrierV2 => {
  if (!subOrder.carrier) {
    throw new Error('carrier not found in subOrder')
  }

  return subOrder.carrier
}
